import * as Sentry from '@sentry/browser';

var ENVIRONMENTS;
(function (ENVIRONMENTS) {
    ENVIRONMENTS["PRODUCTION"] = "production";
    ENVIRONMENTS["STAGING"] = "staging";
    ENVIRONMENTS["DEVELOPMENT"] = "development";
})(ENVIRONMENTS || (ENVIRONMENTS = {}));

async function loadConfigFile (dsn) {
    const dsnPublicKey = dsn.match(/https:\/\/([^@]+)@/)?.[1];
    return new Promise(async (resolve) => {
        try {
            //
            const response = await fetch(`https://observability.hotmart.com/config-files/${dsnPublicKey}.json`);
            const blob = await response.blob();
            const read = new FileReader();
            read.onload = function() {
                resolve(JSON.parse(read.result));
            };
            read.readAsText(blob);
        }
        catch(e) {
            resolve({});
        }
    });
}

const commonIgnorableErrors = [
    /OPTIMIZELY/i,
    'Network Error',
    'Error: Network Error',
    'Network request failed',
    'Failed to fetch',
    'Load failed',
    'NetworkError',
    'Request failed with status code 500',
    'AbortError: The transaction was aborted, so the request cannot be fulfilled.',

    // Random plugins/extensions
    'top.GLOBALS',
    /* eslint-disable quotes */
    "Unexpected token '<'",
    'Extension context invalidated.',
    "SecurityError: Failed to read the 'cssRules' property from 'CSSStyleSheet': Cannot access rules",
    "Cannot read property 'isMew' of undefined",
    "Cannot read property 'removeChild' of null",
    'Load timeout for modules: main http://requirejs.org/docs/errors.html#timeout',
    "Cannot set property 'className' of null",
    "Cannot read property 'toolBar' of undefined",
    "Cannot read property 'query' of undefined",
    "Cannot read property 'NaN' of undefined",
    "Identifier 'originalPrompt' has already been declared",
    'There is no clipping info for given tab',
    'x5onSkinSwitch is not defined',
    'KasperskyLab is not defined',
    'ibPauseAllVideos is not defined',
    'ibFindAllVideos is not defined',
    "undefined is not an object (evaluating 'safari.self.tab.setContextMenuEventUserInfo')",

    // iOS error related to google app search
    "null is not an object (evaluating 'elt.parentNode')",

    // Meteor ERRORS
    'INVALID_STATE_ERR',
    'NS_ERROR_SOCKET_CREATE_FAILED',

    // ignoring requests without object
    'Non-Error promise rejection captured',

    // ignoring gtm.js errors
    'No error message',
    'elements.forEach is not a function',
    "Cannot read property 'innerText' of null",
    "Object doesn't support property or method 'forEach'",
    "Unable to get property 'innerText' of undefined or null reference",
    "null is not an object (evaluating 'element.innerText')",
    "Identifier 'selectors' has already been declared",
    'fbq is not defined',
    "Can't find variable: fbq",

    // avast extension error
    "Cannot read property '_avast_submit' of undefined",

    // Firebase connection errors - omnichat
    'UnknownError: An internal error was encountered in the Indexed Database server',
    'UnknownError: Attempt to get a record from database without an in-progress transaction',
    'PERMISSION_DENIED: Permission denied',
    'Uncaught Error in onSnapshot: FirebaseError: Missing or insufficient permissions.',
    "The project's quota for this operation has been exceeded.",
    'INTERNAL ASSERTION FAILED: Unexpected state',
    'UnknownError: Database deleted by request of the user',

    // Request aborted warnings in console from @hotmart/request
    'Request aborted',

    // External errors
    "Cannot read property 'campaign' of undefined",

    // Navigator share api canceled reject error
    'AbortError: Share canceled',
    'AbortError: Internal error: could not connect to Web Share interface.',
    'AbortError: Abort due to cancellation of share.',

    "null is not an object (evaluating 'a[w.checkoutSettingKeys.devToolsEnabled]')"
];

const commonDenyUrls = [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i,
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    // Google
    /google.com/i,
    /gtm.js/i,
];

const EXTRA_KEY = "ROUTE_TO";
const transport = Sentry.makeMultiplexedTransport(Sentry.makeFetchTransport, (args) => {
    const event = args.getEvent(['event', 'transaction', 'replay_event']);
    if (event &&
        event.extra &&
        EXTRA_KEY in event.extra &&
        Array.isArray(event.extra[EXTRA_KEY])) {
        return event.extra[EXTRA_KEY];
    }
    return [];
});
const initSentry = async ({ dsn, environment, release, debug = false, dynamicSetup = false, disableCaptureConsoleIntegration = false }) => {
    try {
        const isEnvProduction = environment === ENVIRONMENTS.PRODUCTION;
        const customConfig = dynamicSetup ? await loadConfigFile(dsn) : {};
        Sentry.init({
            dsn,
            environment,
            release,
            debug,
            integrations: [
                Sentry.moduleMetadataIntegration(),
                Sentry.browserTracingIntegration(),
                ...(disableCaptureConsoleIntegration ?
                    [] :
                    [Sentry.captureConsoleIntegration({ levels: ['error'] })]),
                Sentry.httpClientIntegration()
            ],
            transport,
            beforeSend: (event) => {
                var _a, _b, _c;
                if ((_c = (_b = (_a = event === null || event === void 0 ? void 0 : event.exception) === null || _a === void 0 ? void 0 : _a.values) === null || _b === void 0 ? void 0 : _b[0].stacktrace) === null || _c === void 0 ? void 0 : _c.frames) {
                    const frames = event.exception.values[0].stacktrace.frames;
                    // Find the last frame with module metadata containing a DSN
                    const routeTo = frames
                        .filter((frame) => frame.module_metadata && frame.module_metadata.dsn)
                        .map((v) => v.module_metadata)
                        .slice(-1); // using top frame only - you may want to customize this according to your needs
                    if (routeTo.length) {
                        event.extra = Object.assign(Object.assign({}, event.extra), { [EXTRA_KEY]: routeTo });
                    }
                }
                return event;
            },
            ignoreErrors: commonIgnorableErrors,
            denyUrls: commonDenyUrls,
            tracesSampleRate: isEnvProduction ? customConfig.tracesSampleRate || 0 : 0,
            profilesSampleRate: 0,
        });
    }
    catch (error) {
        console.error(error);
    }
};

const HotmartRum = {
    init(options) {
        return initSentry(options);
    },
    captureException(exception, hint) {
        return Sentry.captureException(exception, hint);
    },
    captureMessage(message, captureContext) {
        return Sentry.captureMessage(message, captureContext);
    },
    getCurrentScope() {
        return Sentry.getCurrentScope();
    },
    setTag(key, value) {
        return Sentry.setTag(key, value);
    },
    setUser(user) {
        return Sentry.setUser(user);
    },
    withScope(callback) {
        return Sentry.withScope(callback);
    }
};

export { HotmartRum };
//# sourceMappingURL=index.esm2017.js.map
